import React from "react";
import {Alert, Container, Spinner} from "react-bootstrap";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import ZeitraumWahl from "../../Components/EntgangenerUmsatz/ZeitraumWahl.jsx";
import ContainerkundenKacheln from "../../Components/Cosmos/Containerkunden/ContainerkundenKacheln.jsx";
import ContainerkundenTabelle from "../../Components/Cosmos/Containerkunden/ContainerkundenTabelle.jsx";
import useAnalysisData from "../../Components/SalesAnalysis/useAnalysisData.js";
import {useQueryState} from "../../Components/SalesAnalysis/useQueryState.js";
import {berechnePresetZeitraum} from "../../Components/EntgangenerUmsatz/format.js";

const LAUFENDES_JAHR = berechnePresetZeitraum("laufendes_jahr");

/**
 * Containerkunden-Übersicht (Meeting 22.09.2026 Punkt 4,
 * docs/tasks/2026-09-18-cosmos-bereich/UMSETZUNG-NACH-MEETING.md
 * Punkt 6): "Wenn diese Kunden aus der Saisonalität herausfallen, soll
 * trotzdem sichtbar bleiben, was sie tun." Zeigt je gepflegtem
 * Containerkunden, wer wann wie viel je Farbton bestellt hat - unabhängig
 * vom gewählten Zeitraum (ZeitraumWahl, wie auf "Entgangener Umsatz").
 * Zeitraum liegt in der URL, damit ein geteilter Link denselben Stand
 * zeigt.
 */
export default function CosmosContainerkunden() {
    const [preset, setPreset] = useQueryState("zeitraum", "laufendes_jahr");
    const [von, setVon] = useQueryState("von", LAUFENDES_JAHR.von);
    const [bis, setBis] = useQueryState("bis", LAUFENDES_JAHR.bis);

    const wechslePreset = (neuerPreset) => {
        setPreset(neuerPreset);
        if (neuerPreset !== "frei") {
            const zeitraum = berechnePresetZeitraum(neuerPreset);
            setVon(zeitraum.von);
            setBis(zeitraum.bis);
        }
    };

    const setzeVon = (wert) => {
        setPreset("frei");
        setVon(wert);
    };

    const setzeBis = (wert) => {
        setPreset("frei");
        setBis(wert);
    };

    const {data, loading, error} = useAnalysisData("/cosmos/daten/containerkunden", {von, bis});

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="containerkunden">
                Zehn Kunden im weiteren Ausland bestellen sehr große Mengen auf einmal, mit stark schwankendem
                Bestellabstand - ihre Bestellungen werden bei den Saisonfaktoren nicht als Ausschlag gewertet,
                sondern linear über das Jahr verteilt (siehe „Quellen und Berechnungslogik"). Diese Seite zeigt
                unabhängig davon, was diese Kunden tatsächlich je Farbton und Zeitraum bestellt haben.
            </PurposeBox>
            <h1 className="h4 mb-2">
                <HeaderWithHelp help="Bestellte Menge je Containerkunde und Farbton im gewählten Zeitraum - unabhängig von ihrer Sonderbehandlung bei den Saisonfaktoren.">
                    Containerkunden
                </HeaderWithHelp>
            </h1>

            <div className="mb-3">
                <ZeitraumWahl
                    preset={preset}
                    onPresetChange={wechslePreset}
                    von={von}
                    bis={bis}
                    onVonChange={setzeVon}
                    onBisChange={setzeBis}
                    min={data?.grenzen?.min}
                    max={data?.grenzen?.max}
                />
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading && !data && (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            )}

            {data && (
                <>
                    <ContainerkundenKacheln kunden={data.kunden} />
                    <ContainerkundenTabelle kunden={data.kunden} />
                </>
            )}
        </Container>
    );
}
